/**
 * 既存画面への導線（C3_PARTNER-358）
 *
 * サイドメニューに出ている画面にだけリンクする。
 * 権限のない画面へ飛ばすと PrivateRoute が 404 に送るため、出す前に持っているか確かめる。
 * ログイン時に PrivateRoute が sessionStorage の menuItems へ保存した一覧を読む。
 */

/** 既存画面のパス。DB の menu_hierarchy2.href と合わせる */
export const EXISTING_PATH = {
  dailyShift: '/dailyShift',
  staffs: '/staffs',
  staffsEmployee: '/staffs/employee',
  shiftPattern: '/masterShiftPattern',
  /** 予算編成は企業によって出ているメニューが違う */
  salesBudgetInput: '/salesBudgetYearInput',
  salesBudgetYear: '/salesBudgetYear',
};

let cachedRaw: string | null = null;
let cachedPaths: { [path: string]: boolean } = {};

const paths = (): { [path: string]: boolean } => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem('menuItems');
  } catch (e) {
    return {};
  }
  if (raw === cachedRaw) {
    return cachedPaths;
  }
  const found: { [path: string]: boolean } = {};
  try {
    const items = raw ? JSON.parse(raw) : [];
    (Array.isArray(items) ? items : []).forEach((parent: any) => {
      if (!parent) {
        return;
      }
      if (parent.href) {
        found[parent.href] = true;
      }
      if (parent.to) {
        found[parent.to] = true;
      }
      const children = Array.isArray(parent.hierarchyItems) ? parent.hierarchyItems : [];
      children.forEach((child: any) => {
        if (!child) {
          return;
        }
        if (child.to) {
          found[child.to] = true;
        }
        if (child.href) {
          found[child.href] = true;
        }
      });
    });
  } catch (e) {
    // 壊れていたらリンクを出さない
    cachedRaw = raw;
    cachedPaths = {};
    return cachedPaths;
  }
  cachedRaw = raw;
  cachedPaths = found;
  return cachedPaths;
};

/** その画面がサイドメニューに出ているか */
export const hasMenu = (path: string): boolean => !!paths()[path];

/** 候補のうち最初に見つかったパスを返す。どれも無ければ null */
export const firstMenu = (...candidates: Array<string>): string | null => {
  const found = paths();
  for (let i = 0; i < candidates.length; i += 1) {
    if (found[candidates[i]]) {
      return candidates[i];
    }
  }
  return null;
};

/** スタッフマスタ（本社用が無ければ店舗用） */
export const staffMasterPath = (): string | null => firstMenu(EXISTING_PATH.staffs, EXISTING_PATH.staffsEmployee);

/** 予算編成（企業ごとにどちらか） */
export const salesBudgetPath = (): string | null => firstMenu(
  EXISTING_PATH.salesBudgetInput, EXISTING_PATH.salesBudgetYear,
);

/**
 * 勤務スケジュールの URL。ルートは /dailyShift/:orgName?/:orgCode?/:targetDate?
 * 組織名のスラッシュは受け取り側が %2F を戻すのでエンコードして渡す
 */
export const dailyShiftPath = (orgName: string, orgCode: string, targetDate: string): string | null => {
  if (!hasMenu(EXISTING_PATH.dailyShift)) {
    return null;
  }
  if (!orgCode || !targetDate) {
    return EXISTING_PATH.dailyShift;
  }
  return `${EXISTING_PATH.dailyShift}/${encodeURIComponent(orgName || '-')}/${encodeURIComponent(orgCode)}/${targetDate}`;
};
